import { useMedplum } from '@medplum/react';
import { fetchBot } from '@/services/services';
import { useEffect, useMemo, useState } from 'react';
import { GetZoomHostLinkResponse } from '@bariendo/lib/scheduling';

const ONE_HOUR_MS = 60 * 60 * 1000;
const ONE_DAY_MS = 24 * ONE_HOUR_MS;

/**
 * Fetches a fresh Zoom host (start) URL when the appointment is within one hour of starting or has started
 * within the last 24 hours. The host link is hidden before the one-hour window and after the 24-hour grace
 * period. Patient join URL should always be shown from FHIR; this hook is only for the expiring host link.
 */
export function useZoomHostLink(joinUrl: string | undefined, appointmentStart: string | undefined) {
  const medplum = useMedplum();
  const [hostUrl, setHostUrl] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const { isWithinOneHour, isPast } = useMemo(() => {
    if (!appointmentStart) {
      return { isWithinOneHour: false, isPast: false };
    }

    let startMs: number;

    try {
      startMs = new Date(appointmentStart).getTime();
    } catch (_error) {
      return { isWithinOneHour: false, isPast: false };
    }

    if (Number.isNaN(startMs)) {
      return { isWithinOneHour: false, isPast: false };
    }

    const delta = startMs - Date.now();
    return {
      isPast: delta < -ONE_DAY_MS,
      isWithinOneHour: delta >= -ONE_DAY_MS && delta <= ONE_HOUR_MS,
    };
  }, [appointmentStart]);

  useEffect(() => {
    if (!isWithinOneHour || !joinUrl) {
      setHostUrl(null);
      setIsLoading(false);
      return;
    }

    let cancelled = false;
    setIsLoading(true);
    setHostUrl(null);

    async function loadHostUrl() {
      try {
        const res = await fetchBot<GetZoomHostLinkResponse>(medplum, 'get-zoom-host-link', { joinUrl });
        if (!cancelled) {
          setHostUrl(res.hostUrl);
        }
      } catch {
        if (!cancelled) {
          setHostUrl(null);
        }
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    }

    loadHostUrl();

    return () => {
      cancelled = true;
    };
  }, [medplum, isWithinOneHour, joinUrl]);

  return { hostUrl, isLoading, isWithinOneHour, isPast };
}
